import { useEffect, useMemo, useRef, useState } from 'react';
import { GALLERY_IMAGES } from '@/lib/gallery';

function shuffled<T>(arr: ReadonlyArray<T>): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Auto-scrolling photo carousel.
 *
 * Bandwidth budget is the constraint here: the gallery has hundreds of slides, so we
 * cannot let the strip "warm up" the network as it auto-scrolls — that saturates the
 * browser's per-origin connection pool and starves above-the-fold images on the rest of
 * the page (programs, coaches) of bandwidth, sometimes for many seconds.
 *
 * Rules:
 *  - Render only a small window of slides around the current scroll position. Anything
 *    outside the window collapses back to a placeholder so its `<img>` is removed and
 *    the browser drops the in-flight or decoded resource.
 *  - Mark slide images `fetchpriority="low"` so they never compete with hero/program
 *    images in the browser's request queue.
 *  - The auto-scroll moves at 30 px/s; the window updates as scrollLeft changes.
 */
const SLIDE_WIDTH = 240;
const SLIDE_GAP = 12;
const STRIDE = SLIDE_WIDTH + SLIDE_GAP;
const PIXELS_PER_SECOND = 30;
/** Slides to render to the right of the visible viewport (preload buffer). */
const RIGHT_BUFFER = 2;
/** Slides to keep mounted to the left of the visible viewport (avoids flicker as the strip shifts). */
const LEFT_BUFFER = 1;

export function GalleryCarousel() {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const resumeTimerRef = useRef<number | null>(null);
  // Shuffle once per mount so each page-load (and therefore each route, since the
  // carousel re-mounts when navigating between layout-rendered routes vs the home
  // inline placement) shows a different ordering.
  const images = useMemo(() => shuffled(GALLERY_IMAGES), []);

  const [windowRange, setWindowRange] = useState<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });

  // Track the visible window from scroll position. We compute indices from scrollLeft
  // and clientWidth instead of using IntersectionObserver — IO + auto-scroll combined
  // would fire constantly and we'd still need bookkeeping to know what to evict.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      const left = el.scrollLeft;
      const width = el.clientWidth;
      const firstVisible = Math.max(0, Math.floor(left / STRIDE));
      const lastVisible = Math.min(images.length - 1, Math.ceil((left + width) / STRIDE));
      const start = Math.max(0, firstVisible - LEFT_BUFFER);
      const end = Math.min(images.length - 1, lastVisible + RIGHT_BUFFER);
      setWindowRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [images.length]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const reduced =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;
    if (reduced) return;

    let raf = 0;
    let fallbackTimer = 0;
    let lastTime = performance.now();
    let autoLeft = el.scrollLeft;

    const advance = (t: number) => {
      const dt = Math.min(t - lastTime, 250);
      if (dt <= 0) return;
      lastTime = t;
      if (pausedRef.current) {
        autoLeft = el.scrollLeft;
        return;
      }

      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;

      if (Math.abs(el.scrollLeft - autoLeft) > 2) autoLeft = el.scrollLeft;
      autoLeft += (PIXELS_PER_SECOND * dt) / 1000;
      if (autoLeft >= max) autoLeft = 0;
      el.scrollLeft = autoLeft;
    };

    const tick = (t: number) => {
      advance(t);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    fallbackTimer = window.setInterval(() => {
      const now = performance.now();
      if (now - lastTime > 300) advance(now);
    }, 250);
    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(fallbackTimer);
    };
  }, []);

  const pause = () => {
    pausedRef.current = true;
    if (resumeTimerRef.current !== null) window.clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = window.setTimeout(() => {
      pausedRef.current = false;
      resumeTimerRef.current = null;
    }, 3000);
  };
  const resume = () => {
    pausedRef.current = false;
    if (resumeTimerRef.current !== null) {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (resumeTimerRef.current !== null) window.clearTimeout(resumeTimerRef.current);
    };
  }, []);

  const pauseForPointer = () => {
    pause();
    window.addEventListener('pointerup', resume, { once: true });
    window.addEventListener('pointercancel', resume, { once: true });
  };

  return (
    <section aria-label="Gym photo gallery" className="border-t border-bone/10 bg-neutral-950 py-8">
      <div className="container-x">
        <h3 className="mb-4 font-display text-xl tracking-wide text-bone/80">From the gym</h3>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto px-4 pb-4 sm:px-6 lg:px-8"
        style={{ scrollbarWidth: 'thin' }}
        data-testid="gallery-carousel"
        onMouseEnter={pause}
        onMouseLeave={resume}
        onPointerDown={pauseForPointer}
        onPointerUp={resume}
        onPointerCancel={resume}
      >
        {images.map((src, idx) => {
          const inWindow = idx >= windowRange.start && idx <= windowRange.end;
          return <Slide key={src} src={src} loaded={inWindow} />;
        })}
      </div>
    </section>
  );
}

// React 18.3 doesn't recognize the `fetchPriority` prop at runtime (it lands in React 19).
// We set the lowercase HTML attribute via ref so the browser sees the priority hint
// without React warning about an unknown prop.
function setLowFetchPriority(el: HTMLImageElement | null) {
  if (el) el.setAttribute('fetchpriority', 'low');
}

function Slide({ src, loaded }: { src: string; loaded: boolean }) {
  return (
    <div
      className="aspect-[4/5] shrink-0 overflow-hidden rounded-md bg-neutral-900"
      style={{ width: SLIDE_WIDTH }}
      data-testid="gallery-slide"
    >
      {loaded ? (
        <img
          ref={setLowFetchPriority}
          src={src}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="h-full w-full" aria-hidden="true" />
      )}
    </div>
  );
}
